import React, { useState, useEffect, useRef } from 'react';
import { Icons, Button } from '../../../components/ui';

const OTPAuth = ({ phoneNumber, onVerify, onResend, otpSentTime }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [remainingTime, setRemainingTime] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [message, setMessage] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const [isVerifying, setIsVerifying] = useState(false);


  useEffect(() => {
    if (otpSentTime) {
      const elapsedTime = Math.floor((Date.now() - otpSentTime) / 1000);
      const newRemainingTime = Math.max(180 - elapsedTime, 0);
      setRemainingTime(newRemainingTime);
      setMessage('');
      setIsDisabled(false);
      setOtp(['', '', '', '', '', '']);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      if (newRemainingTime > 0) {
        timerRef.current = setInterval(() => {
          setRemainingTime((prevTime) => {
            if (prevTime <= 1) {
              clearInterval(timerRef.current);
              setMessage('OTP has expired. Please request a new one.');
              setIsDisabled(true);
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [otpSentTime]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    let timer;
    if (cooldownTime > 0) {
      timer = setTimeout(() => setCooldownTime((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldownTime]);

  const handleChange = (index, value) => {
    if (isNaN(value)) return;

    // Handle pasted content from mobile keyboard
    if (value.length > 1) {
      const pastedData = value.slice(0, 6).replace(/\D/g, '');
      if (pastedData) {
        const newOtp = Array(6).fill('');
        for (let i = 0; i < 6; i++) {
          newOtp[i] = pastedData[i] || '';
        }
        setOtp(newOtp);

        // Focus on the last filled input or the next empty one
        const nextEmptyIndex = newOtp.findIndex((val) => val === '');
        const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
        inputRefs.current[focusIndex]?.focus();

        // Add highlight effect
        inputRefs.current.forEach((input) => input?.classList.add('highlight'));
        setTimeout(() => {
          inputRefs.current.forEach((input) => input?.classList.remove('highlight'));
        }, 500);

        return;
      }
    }

    // Handle single character input
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'Enter' && otp.join('').length === 6) {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');

    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);

      const nextEmptyIndex = newOtp.findIndex((val) => val === '');
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();

      inputRefs.current.forEach((input) => input?.classList.add('highlight'));
      setTimeout(() => {
        inputRefs.current.forEach((input) => input?.classList.remove('highlight'));
      }, 500);
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length === 6) {
      if (remainingTime === 0) {
        setMessage('OTP has expired. Please request a new one.');
        setIsDisabled(true);
        return;
      }
      if (attempts < 3) {
        setIsVerifying(true);
        try {
          const result = await onVerify(otpString);
          if (result.success) {
            setMessage('OTP verified successfully!');
            setIsDisabled(true);
            clearInterval(timerRef.current);
          } else {
            setMessage(result.message || 'Incorrect OTP. Please try again.');
            setAttempts((prev) => prev + 1);
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
          }
        } catch (error) {
          setMessage('Error verifying OTP. Please try again.');
          setAttempts((prev) => prev + 1);
          setOtp(['', '', '', '', '', '']);
          inputRefs.current[0]?.focus();
        } finally {
          setIsVerifying(false);
        }
      } else {
        setCooldownTime(180);
        setAttempts(0);
        setMessage('Too many attempts. Please wait before trying again.');
        setIsDisabled(true);
      }
    }
  };

  const handleResend = () => {
    if (cooldownTime === 0 && remainingTime === 0) {
      onResend();
    }
  };

  const isSuccess = message.includes('success');

  return (
    <div className="fade-in w-full max-w-md mx-auto my-8 px-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-card p-7 sm:p-8">
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldownTime > 0 || remainingTime > 0}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500"
        >
          <Icons.ChevronLeft className="w-3.5 h-3.5" />
          Resend code
        </button>

        <h2 className="text-lg font-bold text-slate-900">Enter verification code</h2>
        <p className="text-sm text-slate-500 mt-1">
          Please enter the 6-digit OTP sent to{' '}
          <span className="font-medium text-slate-700">{phoneNumber}</span>.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleVerify();
          }}
          className="mt-6 space-y-4"
        >
          <div className="flex justify-center gap-2 sm:gap-3">
            {otp.map((digit, index) => (
              <input
                ref={(el) => (inputRefs.current[index] = el)}
                key={index}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={digit}
                maxLength={index === 0 ? 6 : 1}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-12 sm:w-14 sm:h-14 text-center text-xl font-semibold bg-white border border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 focus:outline-none transition"
              />
            ))}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isDisabled || otp.join('').length < 6 || isVerifying}
          >
            {isVerifying ? 'Verifying...' : 'Verify & continue'}
          </Button>

          {message && (
            <p className={`text-center text-sm ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
              {message}
            </p>
          )}

          {remainingTime > 0 && (
            <p className="text-center text-xs text-slate-400">OTP expires in {remainingTime}s</p>
          )}
          {cooldownTime > 0 && (
            <p className="text-center text-xs text-slate-400">Cooldown time: {cooldownTime}s</p>
          )}

          <p className="text-center text-xs text-slate-500">
            Didn't get it?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldownTime > 0 || remainingTime > 0}
              className="text-brand-600 hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
            >
              Resend
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default OTPAuth;
